import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { sanitize, validateEmail } from '../../utils';
import useDataStore from '../Data/dataStore';
import { API, isMobileWidth, MOBILE_SAFE_RESIZE } from '../globalUtils';
import Div from '../Page/Div';
import { TextInput } from '../UI/TextInput';
import Footer from '../Footer/Footer';
import FixedContainer from '../Page/FixedContainer';
import Loader from '../UI/Loader';
import { ArrowGraphics } from '../Menu/ArrowGraphics';
import { post } from '../../sharedFetchUtils';
const groupACFInputs = (inputs, isCompactView) => {
    if (!inputs)
        return [];
    const groups = inputs.reduce((acc, input) => {
        // `compact_order` is unset on the first group, which ACF sent as `''` and
        // Math.floor turned into 0. Pruned content omits the key instead, and
        // Math.floor(undefined) is NaN -- those inputs landed on a non-numeric
        // array key and were dropped by the filter below, taking two mandatory
        // fields out of the form. Number(...) || 0 restores the old grouping.
        const index = Math.floor(Number(isCompactView ? input.compact_order : input.order) || 0);
        // If no group exists at this index, create it
        if (!acc[index]) {
            acc[index] = [];
        }
        acc[index].push(input);
        return acc;
    }, []);
    // Optionally, filter out any empty spots if the floor values are not continuous
    return groups.filter((group) => group !== undefined);
};
const ConnectSection = ({ content, idx, allowOpen, hasFooter, }) => {
    const setupApi = useDataStore((state) => state.data[API.setup]);
    const productsApi = useDataStore((state) => state.data[API.products]?.products);
    const { inputs, connect_button, connect_legal, connect_success, connect_fail, connect_categories_title, extra_categories, } = setupApi ?? {};
    const c = (hasFooter ? content?.footer : content?.connect) ?? {};
    const { title, details, display, background_animation, use_categories, use_legal, } = c;
    const isInPage = display === 'in_page' || hasFooter;
    const [inputErrors, setInputErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [sending, setSending] = useState(false);
    const [fail, setFail] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const showScrollButtonRef = useRef(showScrollButton);
    showScrollButtonRef.current = showScrollButton;
    const [selectedCategories, setSelectedCategories] = useState({});
    const isCompactView = isInPage;
    const inputGroups = groupACFInputs(inputs, isCompactView);
    const connectInputsRef = useRef(null);
    const mounted = useRef(false);
    const snappy = content.force_snap;
    const wrapperRef = useRef(null);
    const containerRef = useRef(null);
    const categories = [
        ...(productsApi ?? []).map((cat) => cat.name),
        ...(extra_categories ?? []).map((cat) => cat.name),
    ];
    const [mobileView, setMobileView] = useState(isMobileWidth());
    const mobileViewRef = useRef(mobileView);
    mobileViewRef.current = mobileView;
    useEffect(() => {
        window.addEventListener(MOBILE_SAFE_RESIZE, onResize);
        containerRef.current?.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener(MOBILE_SAFE_RESIZE, onResize);
            containerRef.current?.removeEventListener('scroll', onScroll);
        };
    }, [allowOpen]);
    useEffect(() => {
        if (allowOpen) {
            onResize();
        }
    }, [allowOpen]);
    const onScroll = () => {
        requestAnimationFrame(onAfterScroll);
    };
    const onAfterScroll = () => {
        if (wrapperRef.current && containerRef.current) {
            const wrapperBounds = wrapperRef.current?.getBoundingClientRect();
            const scrollGap = containerRef.current.scrollHeight -
                containerRef.current?.scrollTop -
                wrapperBounds.height;
            const hide = scrollGap <= 1;
            if (showScrollButtonRef.current && hide) {
                setShowScrollButton(false);
            }
            if (!showScrollButtonRef.current && !hide) {
                setShowScrollButton(true);
            }
            //console.log('====', showScrollButtonRef.current, scrollGap);
        }
    };
    const onResize = () => {
        const mobile = isMobileWidth();
        if (mobile && !mobileViewRef.current) {
            setMobileView(true);
        }
        if (!mobile && mobileViewRef.current) {
            setMobileView(false);
        }
        onScroll();
    };
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    });
    const handleScroll = () => {
        if (containerRef.current && wrapperRef.current) {
            const wrapperBounds = wrapperRef.current?.getBoundingClientRect();
            const scrollTarget = Math.max(containerRef.current.scrollHeight, containerRef.current.scrollTop + wrapperBounds.height);
            const scrollToTop = {
                behavior: 'smooth',
                top: scrollTarget,
            };
            containerRef.current.scrollTo(scrollToTop);
        }
    };
    const onSubmit = () => {
        const inputElements = connectInputsRef.current?.querySelectorAll('input, textarea');
        if (inputElements && inputElements.length) {
            const messages = Array.from(inputElements).reduce((acc, element) => {
                return { ...acc, [element.name]: element.value };
            }, {});
            let block = false;
            inputs.forEach((input) => {
                const { email_validation, id, mandatory } = input;
                const content = messages[id];
                if (mandatory && !content) {
                    block = true;
                    setInputErrors((state) => ({ ...state, [id]: (state[id] ?? 0) + 1 }));
                }
                if (email_validation) {
                    const isValidEmail = validateEmail(content);
                    if (!isValidEmail) {
                        block = true;
                        setInputErrors((state) => ({
                            ...state,
                            [id]: (state[id] ?? 0) + 1,
                        }));
                    }
                }
            });
            const selectedArray = Object.keys(selectedCategories);
            messages['products'] = selectedArray.length
                ? selectedArray.join(', ').replace(/™/g, '')
                : '';
            if (!block) {
                console.log('Send Messages: ', messages);
                setSending(true);
                fetch(post, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(messages),
                })
                    .then((response) => response.json())
                    .then((data) => {
                    console.log('Message Sent:', data);
                    if (mounted.current) {
                        setSending(false);
                        setSuccess(true);
                        Array.from(inputElements).forEach((element) => {
                            element.value = '';
                        }, {});
                        setSelectedCategories({});
                    }
                })
                    .catch((error) => {
                    if (mounted.current) {
                        console.error('Error:', error);
                        setSending(false);
                        setFail(true);
                    }
                });
            }
        }
    };
    const onCategory = (cat) => {
        if (selectedCategories[cat]) {
            const newCat = { ...selectedCategories };
            delete newCat[cat];
            setSelectedCategories(newCat);
        }
        else {
            setSelectedCategories({ ...selectedCategories, [cat]: true });
        }
    };
    const hasLegal = true; // use_legal
    const blockScale = snappy && isInPage;
    return (_jsxs(_Fragment, { children: [_jsx(FixedContainer, { idx: idx, allowOpen: allowOpen, blocked: !snappy, parentClassName: `connect-container${isInPage ? ' in-page' : ''}${hasFooter ? ' is-footer' : ''}${isCompactView ? ' compact' : ''}`, sliderClassName: `connect-fixed-wrapper`, skipIntro: isInPage, children: [
                    _jsxs("div", { className: "connect-wrapper", id: "connect", ref: wrapperRef, children: [_jsxs("div", { className: "connect-left", children: [title ? (_jsx(Div, { className: "connect-title", delay: 0, noScale: blockScale, children: _jsx("div", { className: "connect-title-wrapper", children: title }) })) : null, _jsx(Div, { className: "connect-details", delay: 1, noScale: blockScale, children: _jsx("div", { dangerouslySetInnerHTML: {
                                                __html: sanitize(details),
                                            } }) }), _jsx(Div, { className: `connect-scroll-button${showScrollButton && snappy ? ' show' : ''}`, delay: 2, noScale: blockScale, onClick: handleScroll, children: _jsx(ArrowGraphics, {}) })] }), _jsxs("div", { className: "connect-right", ref: containerRef, children: [_jsx("div", { className: "connect-inputs", ref: connectInputsRef, children: inputGroups.map((group, idx) => (_jsx("div", { className: "connect-inputs-group", children: group.map((input) => (_jsx(Div, { className: "connect-input-wrapper", delay: idx + 2, noScale: blockScale, children: _jsx(TextInput, { setup: input, error: inputErrors[input.id], allowOpen: allowOpen }) }, input.id))) }, idx))) }), use_categories ? (_jsxs(_Fragment, { children: [_jsx(Div, { className: "connect-categories-title", delay: 5, noScale: blockScale, children: connect_categories_title }, "categories-title"), _jsx(Div, { className: "connect-categories", delay: 6, noScale: blockScale, children: categories.map((cat) => (_jsx("div", { className: `button small-button${Object.keys(selectedCategories).includes(cat) ? ' selected' : ''}`, onClick: () => onCategory(cat), children: cat }, cat))) }, "categories")] })) : null, hasLegal ? (_jsx(Div, { className: "connect-legal alt-font", delay: 7, noScale: blockScale, children: _jsx("div", { dangerouslySetInnerHTML: {
                                                __html: sanitize(connect_legal),
                                            } }) }, "legal")) : null, !!inputGroups.length && (_jsxs(Div, { className: "connect-submit-wrapper", delay: 8, noScale: blockScale, children: [success ? (_jsx("div", { className: "connect-submit-message", children: connect_success })) : fail ? (_jsx("div", { className: "connect-submit-message", children: connect_fail })) : null, !success && !fail && (_jsx("div", { className: `button connect-submit${sending ? ' disabled' : ''}`, onClick: onSubmit, children: sending ? _jsx(Loader, {}) : connect_button }))] }, "submit"))] })] }, 'connect-wrapper'),
                ] }), hasFooter ? (_jsx(Footer, { flexibleHeight: false, idx: idx, animated: true, showBackgroundAnimation: background_animation })) : null] }));
};
export default ConnectSection;
